import type { CommandItem } from "@replo/design-system/utils/commands";
import type { DynamicCommandItem } from "@v2/hooks/useDynamicCommandMenuItems";

import * as React from "react";

import twMerge from "@replo/design-system/utils/twMerge";
import { useDebugOptionsCommandItems } from "@v1/hooks/useDebugOptionsCommandItems";
import { useAdminPanelActions } from "@v2/features/admin/adminPanelStore";
import {
  disableFeatureFlag,
  enableFeatureFlag,
  isFeatureEnabled,
} from "@v2/features/infra/featureFlags";
import {
  useDynamicCommandMenuItem,
  useDynamicCommandMenuItems,
} from "@v2/hooks/useDynamicCommandMenuItems";
import { useIsDebugMode } from "@v2/hooks/useIsDebugMode";
import { Flag, Trash2 } from "lucide-react";
import { featureFlags } from "replo-utils/lib/featureFlags";

/**
 * Provider component that registers debug and feature flag command menu items.
 * These are global items available in both v1 and v2 editors.
 */
export function DebugCommandMenuItemsProvider() {
  const isDebugMode = useIsDebugMode();
  const { toggle: toggleDebugPanel } = useAdminPanelActions();
  const debugOptionsCommandItems = useDebugOptionsCommandItems();

  // Feature flags submenu
  const featureFlagsItem = React.useMemo(() => {
    if (!isDebugMode) {
      return null;
    }

    return {
      label: "Feature Flags...",
      group: "debug" as const,
      icon: { type: "iconComponent" as const, component: Flag },
      type: "children" as const,
      children: Object.fromEntries(
        featureFlags.map((flag) => {
          const { value } = flag;
          const child: CommandItem = {
            label: `Set ${value} to ${isFeatureEnabled(value) ? "OFF" : "ON"} (Currently ${isFeatureEnabled(value) ? "ON" : "OFF"})`,
            icon: {
              type: "custom" as const,
              content: (
                <div className="flex h-[16px] w-[16px] items-center justify-center">
                  <div
                    className={twMerge(
                      "h-[8px] w-[8px] rounded-full",
                      isFeatureEnabled(value)
                        ? "bg-emerald-soft"
                        : "bg-surface",
                    )}
                  />
                </div>
              ),
            },
            onSelect: () => {
              if (isFeatureEnabled(value)) {
                disableFeatureFlag(value);
              } else {
                enableFeatureFlag(value);
              }
            },
          };
          return [value, child];
        }),
      ),
    };
  }, [isDebugMode]);
  useDynamicCommandMenuItem("debug-feature-flags", featureFlagsItem);

  // Toggle debug panel
  const toggleDebugPanelItem = React.useMemo(() => {
    if (!isDebugMode) {
      return null;
    }

    return {
      label: "Toggle Debug Panel",
      group: "debug" as const,
      icon: { type: "custom" as const, content: <div>🐛</div> },
      onSelect: () => {
        toggleDebugPanel();
      },
    };
  }, [isDebugMode, toggleDebugPanel]);
  useDynamicCommandMenuItem("debug-toggle-panel", toggleDebugPanelItem);

  // Debug options
  const debugOptionsItems = React.useMemo(() => {
    if (!isDebugMode) {
      return {};
    }

    const result: Record<string, DynamicCommandItem> = {};

    Object.entries(debugOptionsCommandItems).forEach(([key, item]) => {
      const itemKey = `debug-option-${key}`;
      result[itemKey] = {
        ...item,
        group: "debugOptions",
      };
    });

    return result;
  }, [isDebugMode, debugOptionsCommandItems]);
  useDynamicCommandMenuItems(debugOptionsItems);

  // Clear announcements from local storage
  const clearAnnouncementsItem = React.useMemo(() => {
    if (!isDebugMode) {
      return null;
    }

    return {
      label: "Clear Announcements",
      group: "debug" as const,
      icon: { type: "iconComponent" as const, component: Trash2 },
      onSelect: () => {
        const keysToRemove: string[] = [];
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key && key.startsWith("replo.announcements")) {
            keysToRemove.push(key);
          }
        }
        keysToRemove.forEach((key) => {
          localStorage.removeItem(key);
        });
      },
    };
  }, [isDebugMode]);
  useDynamicCommandMenuItem(
    "debug-clear-announcements",
    clearAnnouncementsItem,
  );

  return null;
}
